<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';

export default {
  name: 'AceleresCreateCannedModal',
  components: {
    NextButton,
    WootMessageEditor,
    AudioRecorder,
  },
  props: {
    show: { type: Boolean, default: false },
    editing: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: '',
      attachments: [],
      uploading: false,
      uploadProgress: { current: 0, total: 0, percent: 0 },
      recording: false,
      recordingPaused: false,
      audioFormat: AUDIO_FORMATS.MP3,
      modal: {
        showLoading: false,
      },
    };
  },
  validations() {
    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: {},
    };
  },
  computed: {
    hasAttachment() {
      return this.attachments.length > 0;
    },
    isFormValid() {
      return (
        !this.v$.shortCode.$invalid &&
        (this.content.trim().length > 0 || this.hasAttachment)
      );
    },
    isEditing() {
      return !!(this.editing && this.editing.id);
    },
    uploadProgressPercentLabel() {
      return `${this.uploadProgress.percent}%`;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.syncFromEditing();
      }
    },
    editing: {
      deep: true,
      handler() {
        if (this.show) this.syncFromEditing();
      },
    },
  },
  mounted() {
    if (this.show) this.syncFromEditing();
  },
  methods: {
    syncFromEditing() {
      this.shortCode = this.editing?.short_code || '';
      this.content = this.editing?.content || '';
      this.recording = false;
      this.recordingPaused = false;
      this.uploadProgress = { current: 0, total: 0, percent: 0 };
      this.attachments = this.buildAttachmentState(this.editing);
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.recording = false;
      this.recordingPaused = false;
      this.uploadProgress = { current: 0, total: 0, percent: 0 };
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    buildAttachmentState(source) {
      const attachments = Array.isArray(source?.attachments)
        ? source.attachments
        : [];
      if (attachments.length) {
        return attachments
          .filter(item => item?.attachment_url && item?.attachment_signed_id)
          .map((item, index) => this.normalizeAttachment(item, index));
      }
      if (source?.attachment_url && source?.attachment_signed_id) {
        return [this.normalizeAttachment(source, 0)];
      }
      return [];
    },
    normalizeAttachment(item, index = 0) {
      const attachmentUrl = item.attachment_url || item.attachmentUrl;
      const attachmentSignedId =
        item.attachment_signed_id || item.attachmentSignedId;
      const fileName =
        item.file_name ||
        item.fileName ||
        decodeURIComponent(
          attachmentUrl.toString().split('?')[0].split('/').pop()
        );
      const fileType = item.file_type || item.fileType || '';

      return {
        attachment_url: attachmentUrl,
        attachment_signed_id: attachmentSignedId,
        file_name: fileName,
        file_type: fileType,
        position: item.position ?? index,
      };
    },
    attachmentKind(attachment) {
      const type = attachment.file_type || '';
      const url = attachment.attachment_url || '';
      if (
        type.startsWith('image/') ||
        /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)
      )
        return 'image';
      if (
        type.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a|aac)(\?|$)/i.test(url)
      )
        return 'audio';
      if (
        type.startsWith('video/') ||
        /\.(mp4|mov|avi|wmv|webm|mkv)(\?|$)/i.test(url)
      )
        return 'video';
      return 'file';
    },
    async uploadBlob(file, name, type, onUploadProgress) {
      const formData = new FormData();
      formData.append('attachment', file);
      const accountId = this.$store.getters.getCurrentAccountId;
      const response = await axios.post(
        `/api/v1/accounts/${accountId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress,
        }
      );
      this.attachments.push({
        attachment_url: response.data.file_url,
        attachment_signed_id: response.data.blob_id,
        file_name: name || file.name,
        file_type: type || file.type || '',
        position: this.attachments.length,
      });
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      this.uploading = true;
      const totalBytes = files.reduce(
        (total, file) => total + Math.max(file.size || 0, 1),
        0
      );
      let completedBytes = 0;
      this.uploadProgress = { current: 1, total: files.length, percent: 0 };
      try {
        await files.reduce(
          (promise, file, index) =>
            promise.then(async () => {
              const fileBytes = Math.max(file.size || 0, 1);
              this.uploadProgress.current = index + 1;
              await this.uploadBlob(file, undefined, undefined, progress => {
                const loadedBytes = Math.min(progress.loaded || 0, fileBytes);
                this.uploadProgress.percent = Math.round(
                  ((completedBytes + loadedBytes) / totalBytes) * 100
                );
              });
              completedBytes += fileBytes;
              this.uploadProgress.percent = Math.round(
                (completedBytes / totalBytes) * 100
              );
            }),
          Promise.resolve()
        );
      } catch (e) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },
    startRecording() {
      this.recording = true;
      this.recordingPaused = false;
    },
    stopRecording() {
      if (this.$refs.cannedAudioRecorder)
        this.$refs.cannedAudioRecorder.stopRecording();
    },
    cancelRecording() {
      this.recording = false;
      this.recordingPaused = false;
    },
    toggleRecordingPlayPause() {
      if (!this.$refs.cannedAudioRecorder) return;

      if (this.recordingPaused) {
        this.$refs.cannedAudioRecorder.resumeRecording();
      } else {
        this.$refs.cannedAudioRecorder.pauseRecording();
      }
    },
    onRecordingPaused() {
      this.recordingPaused = true;
    },
    onRecordingResumed() {
      this.recordingPaused = false;
    },
    onRecordingError(message) {
      this.recording = false;
      this.recordingPaused = false;
      this.uploading = false;
      useAlert(message || 'Não foi possível processar o áudio gravado.');
    },
    async onAudioRecorded(file) {
      this.recordingPaused = false;
      if (!file?.file) {
        this.recording = false;
        return;
      }

      this.uploading = true;
      this.uploadProgress = { current: 1, total: 1, percent: 0 };
      try {
        await this.uploadBlob(
          file.file,
          `gravacao-${Date.now()}.mp3`,
          'audio/mp3',
          progress => {
            const total = progress.total || file.file.size || 1;
            this.uploadProgress.percent = Math.round(
              ((progress.loaded || 0) / total) * 100
            );
          }
        );
        this.uploadProgress.percent = 100;
      } catch (e) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
        this.recording = false;
      }
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
      this.attachments = this.attachments.map((attachment, position) => ({
        ...attachment,
        position,
      }));
    },
    async saveCannedResponse() {
      this.modal.showLoading = true;
      const payload = {
        short_code: this.shortCode,
        content: this.content,
        attachments: this.attachments.map((attachment, position) => ({
          attachment_url: attachment.attachment_url,
          attachment_signed_id: attachment.attachment_signed_id,
          file_name: attachment.file_name,
          file_type: attachment.file_type,
          position,
        })),
      };

      try {
        if (this.isEditing) {
          await this.$store.dispatch('updateCannedResponse', {
            id: this.editing.id,
            ...payload,
          });
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        } else {
          await this.$store.dispatch('createCannedResponse', payload);
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
        }
        this.$emit('saved');
        this.$emit('close');
        this.resetForm();
      } catch (error) {
        const errorMessage =
          error?.message ||
          (this.isEditing
            ? this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE')
            : this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE'));
        useAlert(errorMessage);
      } finally {
        this.modal.showLoading = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[2147483600] grid min-h-dvh place-items-center overflow-y-auto bg-transparent p-4 backdrop-blur-xl"
      role="presentation"
      @click.self="$emit('close')"
      @keydown.esc="$emit('close')"
    >
      <section
        class="flex max-h-[calc(100dvh-2rem)] w-full max-w-[42rem] flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-[0_30px_90px_rgba(15,23,42,0.45)]"
        role="dialog"
        aria-modal="true"
        :aria-label="
          isEditing ? $t('CANNED_MGMT.EDIT.TITLE') : $t('CANNED_MGMT.ADD.TITLE')
        "
      >
        <header
          class="flex flex-shrink-0 items-start gap-3 border-b border-n-weak px-6 py-5"
        >
          <span
            class="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
          >
            <i class="i-lucide-zap size-5" />
          </span>
          <div class="min-w-0 flex-1">
            <h2 class="m-0 text-lg font-semibold leading-6 text-n-slate-12">
              {{
                isEditing
                  ? $t('CANNED_MGMT.EDIT.TITLE')
                  : $t('CANNED_MGMT.ADD.TITLE')
              }}
            </h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-10">
              {{
                isEditing
                  ? $t('CANNED_MGMT.EDIT.DESC')
                  : $t('CANNED_MGMT.ADD.DESC')
              }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="$t('GENERAL.CLOSE')"
            :title="$t('GENERAL.CLOSE')"
            @click="$emit('close')"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </header>

        <form
          class="flex min-h-0 flex-1 flex-col overflow-hidden"
          @submit.prevent="saveCannedResponse()"
        >
          <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label
                class="mb-2 block text-sm font-medium text-n-slate-12"
                :class="{ error: v$.shortCode.$error }"
              >
                {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
              </label>
              <div class="relative">
                <span
                  class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-semibold text-n-brand"
                  aria-hidden="true"
                >
                  <i class="i-lucide-slash size-4" />
                </span>
                <input
                  v-model="shortCode"
                  autofocus
                  type="text"
                  class="h-11 w-full rounded-xl border border-n-weak bg-n-alpha-1 py-2 pl-7 pr-3 text-sm text-n-slate-12 outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/15"
                  :placeholder="
                    $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')
                  "
                  @blur="v$.shortCode.$touch"
                />
              </div>
            </div>

            <div>
              <label class="mb-2 block text-sm font-medium text-n-slate-12">
                {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
              </label>
              <div
                class="acl-canned-editor-wrap overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/15"
              >
                <WootMessageEditor
                  v-model="content"
                  class="message-editor border-0 [&>div]:px-1"
                  channel-type="Context::Default"
                  enable-variables
                  :enable-canned-responses="false"
                  :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
                />
              </div>
            </div>

            <div>
              <div class="mb-2 flex items-center justify-between">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('CANNED_MGMT.ATTACHMENT.LABEL') }}
                </label>
                <span class="text-xs text-n-slate-9">
                  {{ attachments.length }}
                </span>
              </div>

              <div
                v-if="hasAttachment"
                class="mb-3 max-h-56 space-y-2 overflow-y-auto"
              >
                <div
                  v-for="(attachment, index) in attachments"
                  :key="`${attachment.attachment_signed_id || attachment.attachment_url}-${index}`"
                  class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                  :class="{
                    'acl-canned-attachment--audio':
                      attachmentKind(attachment) === 'audio',
                  }"
                >
                  <img
                    v-if="attachmentKind(attachment) === 'image'"
                    :src="attachment.attachment_url"
                    class="size-12 flex-shrink-0 rounded-lg object-cover"
                    :alt="attachment.file_name"
                  />
                  <div
                    v-else-if="attachmentKind(attachment) === 'audio'"
                    class="acl-canned-attachment__meta flex min-w-0 flex-1 items-center gap-2"
                  >
                    <span
                      class="i-lucide-music text-n-slate-11 flex-shrink-0 size-5"
                    />
                    <span class="text-sm text-n-slate-12 truncate">
                      {{ attachment.file_name }}
                    </span>
                  </div>
                  <div
                    v-else-if="attachmentKind(attachment) === 'video'"
                    class="flex flex-col gap-2 flex-1 min-w-0"
                  >
                    <span
                      class="i-lucide-video text-n-slate-11 flex-shrink-0 size-5"
                    />
                    <span class="text-sm text-n-slate-12 truncate">
                      {{ attachment.file_name }}
                    </span>
                  </div>
                  <div v-else class="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      class="i-lucide-file text-n-slate-11 flex-shrink-0 size-5"
                    />
                    <span class="text-sm text-n-slate-12 truncate">
                      {{ attachment.file_name }}
                    </span>
                  </div>
                  <audio
                    v-if="attachmentKind(attachment) === 'audio'"
                    :src="attachment.attachment_url"
                    controls
                    class="acl-canned-attachment__audio w-full min-w-0"
                  />
                  <video
                    v-if="attachmentKind(attachment) === 'video'"
                    :src="attachment.attachment_url"
                    controls
                    class="max-h-40 max-w-xs flex-1 rounded-lg"
                  />
                  <button
                    type="button"
                    class="acl-canned-attachment__remove ml-auto inline-flex size-8 flex-shrink-0 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
                    :aria-label="$t('GENERAL.DISCARD')"
                    :title="$t('GENERAL.DISCARD')"
                    @click="removeAttachment(index)"
                  >
                    <span class="i-lucide-x size-4" />
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label
                  class="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-n-strong px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
                >
                  <span
                    v-if="uploading"
                    class="i-lucide-loader-2 size-4 animate-spin"
                  />
                  <span v-else class="i-lucide-paperclip size-4" />
                  <span>
                    {{
                      uploading
                        ? $t('CANNED_MGMT.ATTACHMENT.UPLOADING')
                        : $t('CANNED_MGMT.ATTACHMENT.PLACEHOLDER')
                    }}
                  </span>
                  <input
                    type="file"
                    multiple
                    class="hidden"
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.rar,.json,.xml"
                    :disabled="uploading"
                    @change="handleFileSelect"
                  />
                </label>
                <button
                  v-if="!recording"
                  type="button"
                  class="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-dashed border-n-strong px-3 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
                  @click="startRecording"
                >
                  <span class="i-lucide-mic size-4" />
                  <span>{{ $t('CANNED_MGMT.ATTACHMENT.RECORD_AUDIO') }}</span>
                </button>
              </div>

              <div
                v-if="uploading && uploadProgress.total"
                class="mt-3 rounded-xl bg-n-alpha-1 p-3"
                role="progressbar"
                :aria-label="$t('CANNED_MGMT.ATTACHMENT.UPLOADING')"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="uploadProgress.percent"
              >
                <div
                  class="mb-1 flex items-center justify-between text-xs text-n-slate-10"
                >
                  <span>
                    {{
                      $t('CANNED_MGMT.ATTACHMENT.PROGRESS', {
                        current: uploadProgress.current,
                        total: uploadProgress.total,
                      })
                    }}
                  </span>
                  <span class="tabular-nums">
                    {{ uploadProgressPercentLabel }}
                  </span>
                </div>
                <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-3">
                  <div
                    class="h-full rounded-full bg-n-brand transition-[width] duration-200"
                    :style="{ width: `${uploadProgress.percent}%` }"
                  />
                </div>
              </div>

              <div
                v-if="recording"
                class="acl-canned-audio-recorder mt-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
              >
                <AudioRecorder
                  ref="cannedAudioRecorder"
                  :audio-record-format="audioFormat"
                  :is-paused="recordingPaused"
                  @delete="cancelRecording"
                  @playpause="toggleRecordingPlayPause"
                  @pause="onRecordingPaused"
                  @play="onRecordingResumed"
                  @record-error="onRecordingError"
                  @send="stopRecording"
                  @finish-record="onAudioRecorded"
                />
              </div>
            </div>
          </div>

          <footer
            class="flex flex-shrink-0 items-center justify-end gap-2 border-t border-n-weak bg-n-background px-6 py-4"
          >
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="$emit('close')"
            />
            <NextButton
              type="submit"
              :label="
                isEditing
                  ? $t('CANNED_MGMT.EDIT.FORM.SUBMIT')
                  : $t('CANNED_MGMT.ADD.FORM.SUBMIT')
              "
              :disabled="!isFormValid || modal.showLoading || uploading"
              :is-loading="modal.showLoading"
            />
          </footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.acl-canned-attachment--audio {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 2rem;
  gap: 0.5rem;
}

.acl-canned-attachment--audio .acl-canned-attachment__meta {
  grid-column: 1;
  grid-row: 1;
}

.acl-canned-attachment--audio .acl-canned-attachment__audio {
  grid-column: 1 / -1;
  grid-row: 2;
  max-width: none;
}

.acl-canned-attachment--audio .acl-canned-attachment__remove {
  grid-column: 2;
  grid-row: 1;
  margin-left: 0;
}

.acl-canned-audio-recorder {
  :deep(.acl-wa-recorder) {
    width: 100%;
    padding: 0;
  }

  :deep(.acl-wa-recorder-main) {
    width: 100%;
    margin: 0;
    gap: clamp(0.375rem, 2vw, 0.75rem);
  }

  :deep(.acl-wa-recorder-main > .acl-wa-icon-btn:not(.acl-wa-playpause)) {
    order: 1 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-rec-dot) {
    order: 2 !important;
  }

  :deep(.acl-wa-audio-center > .acl-wa-time) {
    order: 3 !important;
  }

  :deep(.acl-wa-audio-center > .acl-wa-wave) {
    order: 4 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-playpause) {
    order: 5 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-tip-wrapper) {
    order: 6 !important;
  }

  :deep(.acl-wa-recorder-main > .acl-wa-send) {
    order: 7 !important;
  }
}

.acl-canned-editor-wrap {
  ::v-deep(.ProseMirror-woot-style) {
    min-height: 12rem;
  }
}

@media (max-width: 767px) {
  .acl-canned-editor-wrap {
    ::v-deep(.ProseMirror-woot-style) {
      min-height: 9rem;
    }
  }
}
</style>
